import { type FC, useEffect, useMemo, useRef, useState } from "react";

import debug from "debug";

import type { CustomDeliveryShippingMethod } from "@jti/commerce";
import { WebComponent } from "@jti/commerce";
import type { ExternalShippingCarrierAddress } from "@jti/commerce-types";
import { Loader } from "@jti/core-components/components/Loader";
import { newrelic } from "@jti/fe-clients";
import { isServerSide } from "@jti/fe-common";
import { useScript, useTranslations } from "@jti/ui";

import { CustomShippingOptionWidgetVariantEmbedded } from "./components/CustomShippingOptionWidgetVariantEmbedded";
import { CustomShippingOptionWidgetVariantModal } from "./components/CustomShippingOptionWidgetVariantModal";

import { WIDGET_EVENT_SET_ADDRESS, WIDGET_EVENT_WIDGET_CRASH } from "./CustomShippingOptionWidget.events";
import type { CustomShippingOptionError } from "./CustomShippingOptionWidget.types";

type CustomShippingOptionWidgetProps = {
  webComponentTagName: string;
  widgetUrl: string;
  onSelectAddress: (address: unknown) => unknown;
  variant?: CustomDeliveryShippingMethod["widgetVariant"];
  selectedAddress?: ExternalShippingCarrierAddress | null;
  addressValidationError?: string;
};

const log = debug("desjti:checkout-v2:CustomShippingOption");

const variantToComponentMap = {
  embedded: CustomShippingOptionWidgetVariantEmbedded,
  modal: CustomShippingOptionWidgetVariantModal,
};

export const CustomShippingOptionWidget: FC<CustomShippingOptionWidgetProps> = ({
  webComponentTagName,
  widgetUrl,
  variant = "embedded",
  selectedAddress,
  onSelectAddress,
  addressValidationError,
}) => {
  const { __ } = useTranslations();
  const webComponentRef = useRef(null);
  const [isWebComponentReady, setIsWebComponentReady] = useState<boolean>(!!customElements.get(webComponentTagName));
  const [widgetError, setWidgetError] = useState<CustomShippingOptionError | undefined>(undefined);

  const { scriptStatus } = useScript({ scriptSrc: widgetUrl, attributes: { id: widgetUrl } });

  const VariantRenderer = useMemo(() => variantToComponentMap[variant], [variant]);
  const selectedAddressPreparedForWidget = selectedAddress ? JSON.stringify(selectedAddress) : "";

  const errorPreparedForWidget = useMemo(() => {
    if (!widgetError && !addressValidationError) {
      return undefined;
    }

    if (widgetError) {
      return JSON.stringify(widgetError);
    }

    return JSON.stringify({
      code: "INVALID_STORE_ADDRESS",
      message: addressValidationError,
    });
  }, [addressValidationError, widgetError]);

  const widgetCrashEventHandler = (event: CustomEventInit) => {
    newrelic.noticeError(new Error(`CustomShippingOptionWidget::error:crash ${event.detail}`));

    const errorMessage = event.detail?.message ?? __("cart.checkout.delivery.custom.error.widget_crash");

    const error: CustomShippingOptionError = {
      message: errorMessage,
      code: "WIDGET_CRASH",
    };

    setWidgetError(error);
  };

  const setAddressEventHandler = (event: CustomEventInit<unknown>) => {
    onSelectAddress(event.detail);
  };

  const handleWebComponentInit = async () => {
    if (isServerSide()) {
      return;
    }

    await customElements.whenDefined(webComponentTagName);

    setIsWebComponentReady(true);
  };

  useEffect(() => {
    handleWebComponentInit();
    // TODO - fix deps arrays values after exhaustive-deps as error - DESP-111178
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    function setEventListeners() {
      if (!isWebComponentReady) {
        return;
      }

      if (!webComponentRef.current) {
        newrelic.noticeError(
          new Error(
            `CustomShippingOptionWidget. Failed to set event listeners. The root element "webComponentRef.current" for web component ${webComponentTagName} is ${webComponentRef.current}`
          )
        );

        return;
      }

      const webComponentElement = webComponentRef.current as HTMLElement;

      webComponentElement.addEventListener(WIDGET_EVENT_SET_ADDRESS, setAddressEventHandler);
      webComponentElement.addEventListener(WIDGET_EVENT_WIDGET_CRASH, widgetCrashEventHandler);

      log(`CustomShippingOptionWidget. Event listeners on ${webComponentTagName} are set`);
    },
    // TODO - fix deps arrays values after exhaustive-deps as error - DESP-111178
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isWebComponentReady, scriptStatus]
  );

  useEffect(() => {
    const isWebComponentInitializingError = scriptStatus === "ready" && !isWebComponentReady;

    if (!isWebComponentInitializingError) {
      return;
    }

    setWidgetError({
      code: "WIDGET_INITIALIZATION_FAIL",
      message: __("cart.checkout.delivery.custom.error.widget_initializing"),
    });

    newrelic.noticeError(new Error(`Failed to initialize widget ${webComponentTagName}.`));
    // TODO - fix deps arrays values after exhaustive-deps as error - DESP-111178
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptStatus]);

  return (
    <VariantRenderer
      webComponentTagName={webComponentTagName}
      widgetUrl={widgetUrl}
      selectedAddress={selectedAddress}
      webComponentRef={webComponentRef}
      scriptStatus={scriptStatus}
      addressValidationError={addressValidationError}
      widgetError={widgetError}
    >
      {scriptStatus === "loading" && <Loader data-testid="custom-delivery-widget-loader" />}

      {scriptStatus === "ready" && (
        <WebComponent
          ref={webComponentRef}
          tagName={webComponentTagName}
          error={errorPreparedForWidget}
          selected-address={selectedAddressPreparedForWidget}
          data-testid="custom-delivery-option-widget"
        />
      )}
    </VariantRenderer>
  );
};
